<template>
    <section class="privacy_consent" :aria-labelledby="`${idPrefix}-title`">
        <h3 :id="`${idPrefix}-title`">{{ isSeminar ? '세미나신청' : '문의하기' }} 개인정보 수집·이용 동의서</h3>
        <p v-if="isSeminar" class="privacy_intro">세미나 신청 및 참석을 위해 최소한의 개인정보 활용 동의가 필요합니다.</p>
        <p v-else class="privacy_intro">
            KICC는 문의하기를 위하여, 아래와 같이 개인정보를 처리하고자 합니다.<br />
            본 동의서는 개인정보 보호법 등 관련 법령에 따라 정보주체에게 개인정보 처리 내용을 안내하고, 필요한 경우 동의를 받기 위한 목적으로 작성되었습니다.
            내용을 자세히 읽으신 후 동의 여부를 결정해 주세요.
        </p>

        <div class="privacy_table_scroll" tabindex="0" role="region" :aria-label="`${isSeminar ? '세미나 신청' : '문의하기'} 필수 개인정보 수집·이용 안내`" data-lenis-prevent>
            <table class="privacy_table">
                <caption>{{ isSeminar ? '세미나 신청' : '문의하기' }} 필수 개인정보 수집·이용 내역</caption>
                <colgroup><col class="type_col" /><col /><col /><col class="period_col" /></colgroup>
                <thead><tr><th scope="col">구분</th><th scope="col">처리 목적</th><th scope="col">처리 항목</th><th scope="col">보유 및 이용 기간</th></tr></thead>
                <tbody>
                    <tr>
                        <th scope="row">필수</th>
                        <td>{{ isSeminar ? '세미나 신청 및 참석 여부에 대한 회신, 숙박시설에 대한 숙박자 명단 제공, 상계처리에 따른 대리점 및 참석자 정보 조회 시 사용' : '유/무선 단말기 신청 및 서비스 이용 문의, 부가서비스와 상품소개 서비스 이용에 따른 불만처리, 오류 해결 등 민원 처리 및 회신 시 사용' }}</td>
                        <td>{{ isSeminar ? '이메일주소, 참석자 이름, 참석자 직급, 휴대폰번호' : '고객명, 휴대폰번호, 이메일, 사업자번호, 가맹점명' }}</td>
                        <td class="privacy_emphasis">{{ isSeminar ? '신청일로부터 1년' : '문의일로부터 1년' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <label class="privacy_check" :for="`${idPrefix}-required`">
            <input :id="`${idPrefix}-required`" type="checkbox" :checked="modelValue" @change="$emit('update:modelValue', $event.target.checked)" />
            <span>개인정보 수집·이용 동의 (필수)</span>
        </label>
        <p class="privacy_notice">위 개인정보 수집·이용에 대한 동의를 거부할 권리가 있습니다.
            <strong>다만, {{ isSeminar ? '필수 동의를 거부할 경우 세미나 신청 및 참석에 제한을 받을 수 있습니다.' : '거부할 경우 문의하기 서비스 이용에 제한을 받을 수 있습니다.' }}</strong>
        </p>

        <template v-if="isSeminar">
            <div class="privacy_table_scroll privacy_optional" tabindex="0" role="region" aria-label="주차 지원 선택 개인정보 수집·이용 안내" data-lenis-prevent>
                <table class="privacy_table">
                    <caption>주차 지원 선택 개인정보 수집·이용 내역</caption>
                    <colgroup><col class="type_col" /><col /><col /><col class="period_col" /></colgroup>
                    <thead><tr><th scope="col">구분</th><th scope="col">처리 목적</th><th scope="col">처리 항목</th><th scope="col">보유 및 이용 기간</th></tr></thead>
                    <tbody><tr><th scope="row">선택</th><td>주차 지원을 위한 차량번호 등록 시 사용</td><td>차량번호</td><td class="privacy_emphasis">신청일로부터 1년</td></tr></tbody>
                </table>
            </div>
            <label class="privacy_check" :for="`${idPrefix}-parking`">
                <input :id="`${idPrefix}-parking`" type="checkbox" :checked="parking" @change="$emit('update:parking', $event.target.checked)" />
                <span>개인정보 수집·이용 동의 (선택)</span>
            </label>
            <p class="privacy_notice">선택 항목에 동의하지 않더라도 기본 서비스 이용에는 제한이 없습니다. 다만, 선택 항목을 활용한 <strong>주차 지원</strong> 이용 또는 제공에 제한을 받을 수 있습니다.</p>

            <div class="privacy_table_scroll privacy_optional" tabindex="0" role="region" aria-label="숙박시설 이용 개인정보 제3자 제공 안내" data-lenis-prevent>
                <table class="privacy_table">
                    <caption>숙박시설 이용 개인정보 제3자 제공 내역</caption>
                    <thead><tr><th scope="col">제공받는 자</th><th scope="col">제공 목적</th><th scope="col">제공 항목</th><th scope="col">보유 및 이용 기간</th></tr></thead>
                    <tbody><tr><th scope="row">숙박업체</th><td class="privacy_emphasis">숙박시설 이용에 따른 명단 제공</td><td>이름, 휴대폰번호, 이메일</td><td class="privacy_emphasis">목적 달성 시</td></tr></tbody>
                </table>
            </div>
            <label class="privacy_check" :for="`${idPrefix}-third-party`">
                <input :id="`${idPrefix}-third-party`" type="checkbox" :checked="thirdParty" @change="$emit('update:thirdParty', $event.target.checked)" />
                <span>개인정보 제3자 제공 동의 (선택)</span>
            </label>
            <p class="privacy_notice">동의를 거부할 권리가 있습니다. 다만, 동의를 거부할 경우 <strong>숙박시설 이용</strong> 또는 제공에 제한을 받을 수 있습니다.<br />위와 같이 개인정보를 제3자에게 제공하는 데 동의하십니까?</p>
        </template>
    </section>
</template>

<script>
export default {
    props: {
        kind: { type: String, default: 'inquiry' },
        idPrefix: { type: String, required: true },
        modelValue: { type: Boolean, default: false },
        parking: { type: Boolean, default: false },
        thirdParty: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'update:parking', 'update:thirdParty'],
    computed: {
        isSeminar() { return this.kind === 'seminar'; },
    },
};
</script>

<style scoped lang="scss">
.privacy_consent {
    width: 100%;
    min-width: 0;
    color: var(--black1);
    line-height: 1.8;
    font-size: 16px;

    h3 { margin: 0 0 24px; font-size: 24px; font-weight: 600; text-align: left; }
    .privacy_intro { margin: 0 0 24px; color: var(--black3); }
    .privacy_table_scroll { width: 100%; overflow-x: auto; }
    .privacy_optional { margin-top: 40px; }
    .privacy_table {
        width: 100%; min-width: 580px; border-collapse: collapse; table-layout: fixed;
        border-top: 2px solid var(--black1);
        caption { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
        .type_col { width: 12%; }
        .period_col { width: 23%; }
        th, td {
            padding: 24px; border-right: 1px solid var(--border-color1); border-bottom: 1px solid var(--border-color1);
            vertical-align: middle; overflow-wrap: anywhere;
        }
        th:last-child, td:last-child { border-right: 0; }
        thead th { padding: 15px 12px; background: #f6f6f6; font-size: 16px; font-weight: 600; text-align: center; }
        tbody th { padding: 24px 12px; font-weight: 500; text-align: center; }
        td { color: var(--black3); font-weight: 400; }
        .privacy_emphasis { color: var(--black1); font-weight: 600; }
    }
    .privacy_check {
        display: flex; align-items: center; gap: 8px; width: fit-content; margin-top: 24px;
        font-size: 16px; font-weight: 500; cursor: pointer;
        input {
            appearance: none; position: static; opacity: 1; width: 20px; height: 20px; margin: 0;
            flex: 0 0 20px; border: 1px solid #333; border-radius: 0; background: var(--white); cursor: pointer;
            &:checked { border-color: var(--orange); background: url('@/assets/check.png') no-repeat center; }
            &:focus-visible { outline: 2px solid var(--black1); outline-offset: 3px; }
        }
    }
    .privacy_notice { margin: 12px 0 0; color: var(--black3); font-size: 14px; }
    .privacy_notice strong { font-weight: 400; }
    @media (max-width: 768px) {
        h3 { margin-bottom: 20px; }
        .privacy_optional { margin-top: 30px; }
        .privacy_table th, .privacy_table td { padding: 16px 12px; }
        .privacy_check { align-items: flex-start; }
        .privacy_check input { margin-top: 4px; }
    }
}
</style>
